import type { Track } from '@pujolisique/shared';
import { api, artUrl, ensureStreamToken, streamUrl } from '../api/client';
import { saveProgress } from '../api/hooks';
import { queryClient } from '../queryClient';
import { toast } from '../ui/toast';
import { currentTrack, setSeekHandler, usePlayer } from './store';

/**
 * Moteur de lecture : deux lecteurs <audio> qui se passent le relais.
 *  - le titre suivant est préparé dans le second lecteur un peu avant la fin ;
 *  - fondu enchaîné entre deux albums différents, enchaînement sans blanc entre
 *    deux titres d'un même album (live, classique, albums mixés) ;
 *  - volume égalisé (ReplayGain des étiquettes, ou volume mesuré par le serveur) ;
 *  - radio infinie : la file se prolonge avec des titres proches ;
 *  - Media Session (écran verrouillé, touches multimédia), historique, reprise
 *    des histoires et recherche dans les flux convertis.
 * Pas de Web Audio : la lecture continue écran verrouillé sur tous les appareils.
 */

interface Deck {
  el: HTMLAudioElement;
  trackId?: string;
  quality?: number;
  /** Position de départ du flux converti en cours (les flux convertis commencent à « start ») */
  offset: number;
  transcoded: boolean;
  /** Facteur du volume égalisé (≤ 1) */
  gain: number;
  /** Facteur du fondu (0 → 1) */
  fade: number;
}

function makeDeck(): Deck {
  const el = new Audio();
  el.preload = 'auto';
  return { el, offset: 0, transcoded: false, gain: 1, fade: 1 };
}

const decks: [Deck, Deck] = [makeDeck(), makeDeck()];

/**
 * iPhone / iPad : le volume d'un lecteur ne se règle pas par programme et chaque
 * lecteur doit être lancé par un geste. On y garde un seul lecteur : ni fondu
 * ni volume égalisé, mais une lecture fiable écran verrouillé.
 */
export const volumeControl = (() => {
  try {
    const probe = new Audio();
    probe.volume = 0.5;
    return Math.abs(probe.volume - 0.5) < 0.01;
  } catch {
    return false;
  }
})();
let active = 0;
const cur = () => decks[active];
const spare = () => decks[1 - active];

let loadedId: string | undefined;
let loadedQuality: number | undefined;
let submitted: string | undefined;
let consecutiveErrors = 0;
let restorePosition: number | undefined;
let lastProgressSave = 0;
/** Un changement de source peut émettre « pause » : on l'ignore brièvement */
let ignorePauseUntil = 0;

/** Titre suivant préparé dans le lecteur de réserve */
let prepared: { deck: Deck; track: Track; ready: boolean } | undefined;
let preparing = false;
/** Façon de quitter le titre en cours au prochain changement (posée juste avant `next(true)`) */
let pendingExit: { kind: 'fade'; seconds: number } | { kind: 'finish' } | undefined;
/** Fondu en cours */
let fadeTimer: number | undefined;
let fadingOut: Deck | undefined;
/** Surveillance fine de la fin du titre (enchaînement sans blanc) */
let gaplessTimer: number | undefined;
/** Radio infinie */
let radioPending: Promise<void> | undefined;
let radioFailedAt = 0;

const BROWSER_UNSUPPORTED = new Set(['wma', 'ape', 'wv', 'aif', 'aiff', 'dsf', 'mka']);

function isTranscoded(t: Track, quality: number) {
  if (quality > 0) return true;
  if (!t.format) return false;
  return BROWSER_UNSUPPORTED.has(t.format) || ((t.format === 'm4a' || t.format === 'm4b') && !!t.bitDepth);
}

const position = () => cur().offset + (cur().el.currentTime || 0);

/** Durée précise du titre en cours (celle du fichier si le navigateur la connaît) */
function exactDuration(t: Track) {
  const d = cur();
  if (!d.transcoded && Number.isFinite(d.el.duration) && d.el.duration > 0) return d.el.duration;
  return t.duration || 0;
}

function artistNames(t: Track) {
  return t.artists.map((a) => a.name).join(', ') || t.albumArtist || '';
}

/* ============================ Volume ============================ */

/** L'auditeur écoute-t-il un album (titres voisins du même album, sans aléatoire) ? */
function inAlbumContext(t: Track) {
  const s = usePlayer.getState();
  if (!t.albumId || s.shuffle) return false;
  const i = s.queue.findIndex((q, k) => q.id === t.id && Math.abs(k - s.index) <= 1);
  if (i < 0) return false;
  return s.queue[i - 1]?.albumId === t.albumId || s.queue[i + 1]?.albumId === t.albumId;
}

/** Facteur de volume égalisé d'un titre (atténuation seulement : jamais de saturation) */
function gainFactor(t: Track | undefined) {
  const s = usePlayer.getState();
  if (!t?.gain || s.normalize === 'off') return 1;
  const album = s.normalize === 'album' || (s.normalize === 'auto' && inAlbumContext(t));
  const db = album ? (t.gain.album ?? t.gain.track) : (t.gain.track ?? t.gain.album);
  if (db == null || !Number.isFinite(db)) return 1;
  return Math.min(1, Math.pow(10, db / 20));
}

function applyVolume(d: Deck) {
  const s = usePlayer.getState();
  const base = s.muted ? 0 : s.volume;
  d.el.volume = Math.max(0, Math.min(1, base * d.gain * d.fade));
}

function trackById(id: string | undefined) {
  if (!id) return undefined;
  return usePlayer.getState().queue.find((q) => q.id === id);
}

function refreshGains() {
  for (const d of decks) {
    d.gain = gainFactor(trackById(d.trackId));
    applyVolume(d);
  }
}

/* ========================= Media Session ========================= */

function updateMediaSession(t: Track | undefined) {
  if (!('mediaSession' in navigator)) return;
  if (!t) {
    navigator.mediaSession.metadata = null;
    return;
  }
  const art = (size: number) => {
    const u = artUrl(t.cover, size);
    return u ? new URL(u, location.href).toString() : undefined;
  };
  const artwork = [96, 256, 512].map((s) => art(s)).filter(Boolean).map((src, i) => ({ src: src!, sizes: ['96x96', '256x256', '512x512'][i], type: 'image/jpeg' }));
  navigator.mediaSession.metadata = new MediaMetadata({ title: t.title, artist: artistNames(t), album: t.album ?? '', artwork });
}

function setupMediaSessionHandlers() {
  if (!('mediaSession' in navigator)) return;
  const ms = navigator.mediaSession;
  const s = () => usePlayer.getState();
  const handlers: [MediaSessionAction, MediaSessionActionHandler][] = [
    ['play', () => s().setPlaying(true)],
    ['pause', () => s().setPlaying(false)],
    ['nexttrack', () => s().next()],
    ['previoustrack', () => s().prev()],
    ['seekto', (d) => d.seekTime !== undefined && s().seek(d.seekTime)],
    ['seekbackward', (d) => s().seek(position() - (d.seekOffset ?? 10))],
    ['seekforward', (d) => s().seek(position() + (d.seekOffset ?? 10))],
  ];
  for (const [a, h] of handlers) {
    try {
      ms.setActionHandler(a, h);
    } catch {
      /* action non supportée */
    }
  }
}

/* ========================= Enchaînements ========================= */

/** Titre qui suivra naturellement le titre en cours */
function upcoming(): Track | undefined {
  const s = usePlayer.getState();
  if (s.repeat === 'one') return undefined;
  if (s.index + 1 < s.queue.length) return s.queue[s.index + 1];
  if (s.repeat === 'all' && s.queue.length > 1) return s.queue[0];
  return undefined;
}

type Transition = 'fade' | 'gapless' | 'cut';

function transitionBetween(a: Track, b: Track): Transition {
  const s = usePlayer.getState();
  // Histoires : on garde chaque épisode entier, sans fondu
  if (a.kind === 'stories' || b.kind === 'stories') return 'cut';
  const sameAlbum = !!a.albumId && a.albumId === b.albumId;
  if (sameAlbum) return s.gapless ? 'gapless' : 'cut';
  if (s.crossfade > 0) return 'fade';
  return s.gapless ? 'gapless' : 'cut';
}

function stopFade() {
  if (fadeTimer) window.clearInterval(fadeTimer);
  fadeTimer = undefined;
  if (fadingOut) {
    fadingOut.el.pause();
    fadingOut.fade = 1;
    fadingOut = undefined;
  }
  cur().fade = 1;
  applyVolume(cur());
}

function stopGaplessWatch() {
  if (gaplessTimer) window.clearInterval(gaplessTimer);
  gaplessTimer = undefined;
}

function dropPrepared() {
  if (!prepared) return;
  const d = prepared.deck;
  prepared = undefined;
  if (d !== cur() && d !== fadingOut) {
    d.el.removeAttribute('src');
    d.el.load();
    d.trackId = undefined;
  }
}

/** Prépare le titre suivant dans le lecteur de réserve (un peu avant la fin) */
async function prepareNext(next: Track) {
  if (!volumeControl || preparing || fadingOut) return;
  preparing = true;
  try {
    await ensureStreamToken().catch(() => undefined);
    const { quality } = usePlayer.getState();
    const d = spare();
    if (upcoming()?.id !== next.id || d === cur()) return;
    d.el.pause();
    d.trackId = next.id;
    d.quality = quality;
    d.transcoded = isTranscoded(next, quality);
    d.offset = 0;
    d.fade = 0;
    d.gain = gainFactor(next);
    applyVolume(d);
    const entry = { deck: d, track: next, ready: false };
    prepared = entry;
    d.el.addEventListener('canplaythrough', () => (entry.ready = true), { once: true });
    d.el.src = streamUrl(next.id, quality, 0);
    d.el.load();
  } finally {
    preparing = false;
  }
}

/** À chaque avancée de la lecture : préparation du suivant, fondu, enchaînement sans blanc */
function onProgress(t: Track) {
  const s = usePlayer.getState();
  const next = upcoming();
  if (!next || next.kind === 'stories' || next.id === t.id) {
    if (prepared) dropPrepared();
    return;
  }
  const dur = exactDuration(t);
  if (!dur) return;
  const remaining = dur - position();
  const mode = transitionBetween(t, next);
  if (prepared && (prepared.track.id !== next.id || prepared.deck.quality !== s.quality)) dropPrepared();
  if (!prepared && remaining < Math.max(30, s.crossfade + 20)) void prepareNext(next);
  if (!prepared || !s.playing || fadeTimer || cur().el.paused) return;
  if (mode === 'fade' && remaining <= s.crossfade + 0.05 && remaining > 0.4 && prepared.ready) {
    pendingExit = { kind: 'fade', seconds: Math.min(s.crossfade, remaining) };
    advance();
  } else if (mode === 'gapless' && !cur().transcoded && remaining <= 0.8 && !gaplessTimer) {
    // Dernières fractions de seconde : surveillance fine, relais au plus près de la fin
    gaplessTimer = window.setInterval(() => {
      const left = exactDuration(t) - position();
      if (currentTrack(usePlayer.getState())?.id !== t.id || !prepared) return stopGaplessWatch();
      if (left <= 0.03) {
        stopGaplessWatch();
        pendingExit = { kind: 'finish' };
        advance();
      }
    }, 8);
  }
}

/** Passe au titre suivant comme à la fin naturelle du titre */
function advance() {
  const t = currentTrack(usePlayer.getState());
  if (t?.kind === 'stories') persistProgress(t, 0, true, true);
  usePlayer.getState().next(true);
}

/**
 * Bascule sur le lecteur de réserve, déjà prêt. Le lecteur sortant s'arrête, se
 * termine seul (sans blanc) ou s'efface en fondu.
 */
function switchToPrepared(t: Track, autoplay: boolean) {
  const p = prepared!;
  prepared = undefined;
  const out = cur();
  const exit = pendingExit;
  pendingExit = undefined;
  stopGaplessWatch();
  if (fadeTimer) stopFade();
  active = decks.indexOf(p.deck);
  const inc = cur();
  inc.gain = gainFactor(t);
  ignorePauseUntil = Date.now() + 1500;
  if (exit?.kind === 'fade' && autoplay) {
    fadingOut = out;
    inc.fade = 0;
    applyVolume(inc);
    const start = performance.now();
    const ms = exit.seconds * 1000;
    fadeTimer = window.setInterval(() => {
      const k = Math.min(1, (performance.now() - start) / ms);
      // Fondu à puissance constante : pas de creux au milieu
      inc.fade = Math.sin((k * Math.PI) / 2);
      if (fadingOut) {
        fadingOut.fade = Math.cos((k * Math.PI) / 2);
        applyVolume(fadingOut);
      }
      applyVolume(inc);
      if (k >= 1) stopFade();
    }, 40);
  } else {
    inc.fade = 1;
    applyVolume(inc);
    // Sans blanc : le sortant joue ses dernières millisecondes ; sinon il s'arrête
    if (exit?.kind !== 'finish') out.el.pause();
  }
  usePlayer.setState({ duration: t.duration, currentTime: 0, buffering: false });
  if (autoplay) {
    inc.el.play().catch((e: DOMException) => {
      if (e.name === 'NotAllowedError') usePlayer.getState().setPlaying(false);
    });
  }
}

/* ============================ Chargement ============================ */

/** Charge une piste ; startAt = reprise (histoire, qualité changée, rechargement de page) */
async function load(t: Track, autoplay: boolean, startAt = 0) {
  const { quality } = usePlayer.getState();
  loadedId = t.id;
  loadedQuality = quality;
  updateMediaSession(t);
  document.title = `${t.title} — ${artistNames(t)}`;

  if (prepared && prepared.track.id === t.id && prepared.deck.quality === quality && startAt === 0) {
    switchToPrepared(t, autoplay);
    return;
  }
  pendingExit = undefined;
  stopGaplessWatch();
  if (fadeTimer) stopFade();
  dropPrepared();
  spare().el.pause();

  await ensureStreamToken().catch(() => undefined);
  if (loadedId !== t.id) return;
  const d = cur();
  d.trackId = t.id;
  d.quality = quality;
  d.transcoded = isTranscoded(t, quality);
  d.offset = d.transcoded ? startAt : 0;
  d.fade = 1;
  d.gain = gainFactor(t);
  applyVolume(d);
  ignorePauseUntil = Date.now() + 1500;
  d.el.src = streamUrl(t.id, quality, d.transcoded ? startAt : 0);
  usePlayer.setState({ duration: t.duration, currentTime: startAt });
  if (!d.transcoded && startAt > 0) d.el.addEventListener('loadedmetadata', () => (d.el.currentTime = startAt), { once: true });
  if (autoplay) {
    try {
      await d.el.play();
    } catch (e) {
      if ((e as DOMException).name === 'NotAllowedError') usePlayer.getState().setPlaying(false);
    }
  }
}

function seek(t: number) {
  if (!loadedId) return;
  stopGaplessWatch();
  if (fadeTimer) stopFade();
  const d = cur();
  if (d.transcoded) {
    // Un flux converti ne se parcourt pas : on le relance à la position demandée
    const track = currentTrack(usePlayer.getState());
    if (!track) return;
    const wasPlaying = usePlayer.getState().playing;
    d.offset = t;
    ignorePauseUntil = Date.now() + 1500;
    d.el.src = streamUrl(track.id, usePlayer.getState().quality, t);
    if (wasPlaying) d.el.play().catch(() => undefined);
  } else {
    d.el.currentTime = t;
  }
}

/** Répétition d'un titre : retour au début et lecture */
function replay() {
  seek(0);
  submitted = undefined;
  cur().el.play().catch(() => usePlayer.setState({ playing: false }));
}

/* ============================ Radio infinie ============================ */

/** Prolonge la file avec des titres proches quand il n'en reste presque plus */
function maybeRadio(force = false): Promise<void> | undefined {
  const s = usePlayer.getState();
  const t = currentTrack(s);
  if (!s.radio || s.repeat !== 'off' || !t || t.kind !== 'music') return undefined;
  if (radioPending) return radioPending;
  if (!force && s.queue.length - s.index - 1 > 2) return undefined;
  if (Date.now() - radioFailedAt < 60_000) return undefined;
  const seeds = s.queue
    .slice(Math.max(0, s.index - 7), s.queue.length)
    .filter((q) => q.kind === 'music')
    .reverse()
    .slice(0, 10)
    .map((q) => q.id);
  const exclude = s.queue.slice(-1500).map((q) => q.id);
  radioPending = api<Track[]>('/api/radio', { method: 'POST', json: { seeds, exclude, limit: 15 } })
    .then((tracks) => {
      const st = usePlayer.getState();
      if (!st.radio || !tracks.length) {
        if (!tracks.length) radioFailedAt = Date.now();
        return;
      }
      const have = new Set(st.queue.map((q) => q.id));
      const fresh = tracks.filter((x) => !have.has(x.id));
      if (fresh.length) usePlayer.setState({ queue: [...st.queue, ...fresh], ...(st.original ? { original: [...st.original, ...fresh] } : {}) });
    })
    .catch(() => {
      radioFailedAt = Date.now();
    })
    .finally(() => {
      radioPending = undefined;
    });
  return radioPending;
}

/* ============================ Historique, reprise ============================ */

function scrobble(id: string) {
  api(`/api/scrobble/${encodeURIComponent(id)}`, { method: 'POST', json: { submission: true } })
    .then(() => {
      queryClient.invalidateQueries({ queryKey: ['history'] });
      queryClient.invalidateQueries({ queryKey: ['home'] });
    })
    .catch(() => undefined);
}

/** Sauvegarde la reprise d'une histoire (toutes les 15 s, à la pause, à la fin) */
function persistProgress(t: Track | undefined, pos: number, finished?: boolean, refresh = false) {
  if (!t || t.kind !== 'stories') return;
  lastProgressSave = Date.now();
  const p = finished ? { position: 0, finished: true } : { position: pos, finished: false };
  // Met à jour la copie de la piste dans la file (reprise locale immédiate)
  usePlayer.setState((s) => ({ queue: s.queue.map((q) => (q.id === t.id ? { ...q, progress: { ...p, updatedAt: Date.now() } } : q)) }));
  saveProgress(t.id, pos, finished)
    .then(() => {
      if (refresh) {
        queryClient.invalidateQueries({ queryKey: ['stories'] });
        queryClient.invalidateQueries({ queryKey: ['series'] });
        queryClient.invalidateQueries({ queryKey: ['home'] });
      }
    })
    .catch(() => undefined);
}

/** Position de reprise d'une histoire (quelques secondes avant l'arrêt) */
function resumePoint(t: Track) {
  if (t.kind !== 'stories' || !t.progress || t.progress.finished) return 0;
  return t.progress.position > 10 ? Math.max(0, t.progress.position - 3) : 0;
}

/* ============================ Démarrage ============================ */

function attach(d: Deck) {
  const el = d.el;
  const isActive = () => d === cur();

  el.addEventListener('timeupdate', () => {
    if (!isActive()) return;
    const s = usePlayer.getState();
    const pos = position();
    usePlayer.setState({ currentTime: pos });
    const t = currentTrack(s);
    if (!t || t.id !== d.trackId) return;
    const dur = t.duration || el.duration;
    if (submitted !== t.id && dur > 0 && (pos >= Math.min(dur / 2, 240) || pos >= dur - 2)) {
      submitted = t.id;
      scrobble(t.id);
    }
    if (t.kind === 'stories' && !el.paused && Date.now() - lastProgressSave > 15_000) persistProgress(t, pos);
    onProgress(t);
  });
  el.addEventListener('durationchange', () => {
    if (isActive() && !d.transcoded && Number.isFinite(el.duration)) usePlayer.setState({ duration: el.duration });
  });
  el.addEventListener('waiting', () => isActive() && usePlayer.setState({ buffering: true }));
  el.addEventListener('playing', () => {
    if (!isActive()) return;
    usePlayer.setState({ buffering: false });
    consecutiveErrors = 0;
    if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'playing';
  });
  el.addEventListener('pause', () => {
    if (!isActive() || Date.now() < ignorePauseUntil) return;
    if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'paused';
    const s = usePlayer.getState();
    if (!el.ended) persistProgress(currentTrack(s), position(), false, true);
    // Pause venue du système (casque débranché…) → reflétée dans le store
    if (!el.ended && s.playing && el.currentTime > 0) usePlayer.setState({ playing: false });
  });
  el.addEventListener('ended', () => {
    // Le titre sortant d'un fondu est terminé : le fondu entrant continue seul
    if (fadingOut === d) {
      d.fade = 1;
      fadingOut = undefined;
    }
    if (!isActive()) return;
    const s = usePlayer.getState();
    // Un seul titre en boucle (« tout répéter ») : on le reprend au début
    if (s.repeat === 'all' && s.queue.length === 1) {
      replay();
      return;
    }
    // Fin de file avec la radio : on attend les titres proches avant de continuer
    if (s.radio && !upcoming() && s.repeat === 'off') {
      const wait = maybeRadio(true);
      if (wait) {
        persistProgress(currentTrack(s), 0, true, true);
        const at = { index: s.index, id: currentTrack(s)?.id };
        void wait.then(() => {
          // Entre-temps, la personne a peut-être choisi autre chose : on ne saute rien
          const now = usePlayer.getState();
          if (now.index === at.index && currentTrack(now)?.id === at.id) now.next(true);
        });
        return;
      }
    }
    advance();
  });
  el.addEventListener('error', () => {
    if (prepared?.deck === d && !isActive()) {
      // Le titre suivant ne se prépare pas : il sera chargé normalement le moment venu
      dropPrepared();
      return;
    }
    if (!isActive()) return;
    const t = currentTrack(usePlayer.getState());
    // Erreur sur la piste restaurée au démarrage (non lancée) : ignorée
    if (!t || !el.src || !usePlayer.getState().playing) return;
    consecutiveErrors++;
    toast(`Lecture impossible : « ${t.title} »`, 'error');
    if (consecutiveErrors < 3) usePlayer.getState().next(true);
    else usePlayer.setState({ playing: false });
  });
}

export function initPlayerEngine() {
  const st = usePlayer.getState();
  restorePosition = st.currentTime > 0 ? st.currentTime : undefined;
  setSeekHandler(seek, replay);
  decks.forEach(attach);
  setupMediaSessionHandlers();

  usePlayer.subscribe((s, prev) => {
    if (s.volume !== prev.volume || s.muted !== prev.muted) for (const d of decks) applyVolume(d);
    if (s.normalize !== prev.normalize || s.shuffle !== prev.shuffle) refreshGains();
    if (s.radio !== prev.radio || s.index !== prev.index || s.queue.length !== prev.queue.length || s.repeat !== prev.repeat) void maybeRadio();

    const t = currentTrack(s);
    if (!t) {
      if (loadedId) {
        stopGaplessWatch();
        if (fadeTimer) stopFade();
        dropPrepared();
        for (const d of decks) {
          d.el.pause();
          d.el.removeAttribute('src');
          d.el.load();
          d.trackId = undefined;
        }
        loadedId = undefined;
        updateMediaSession(undefined);
      }
      return;
    }
    const trackChanged = t.id !== loadedId;
    const qualityChanged = s.quality !== loadedQuality && loadedId === t.id;
    const restarted = !trackChanged && (s.queue !== prev.queue || s.index !== prev.index) && s.currentTime === 0 && prev.currentTime !== 0;
    if (restarted) {
      seek(0);
      submitted = undefined;
      if (s.playing) cur().el.play().catch(() => usePlayer.setState({ playing: false }));
      return;
    }
    if (trackChanged || qualityChanged) {
      // Sauvegarde la position de l'histoire que l'on quitte
      const previous = prev.queue.find((q) => q.id === loadedId);
      if (trackChanged && previous?.kind === 'stories' && !cur().el.ended && position() > 5) persistProgress(previous, position(), false, true);
      let startAt = 0;
      if (qualityChanged) startAt = position();
      else if (restorePosition) {
        startAt = restorePosition;
        restorePosition = undefined;
      } else startAt = resumePoint(t);
      if (trackChanged) submitted = undefined;
      void load(t, s.playing, startAt);
      return;
    }
    // La file a changé autour du titre en cours : contexte d'album et volume à revoir
    if (s.queue !== prev.queue && s.normalize === 'auto') refreshGains();
    if (s.playing !== prev.playing) {
      if (s.playing) cur().el.play().catch(() => usePlayer.setState({ playing: false }));
      else {
        stopGaplessWatch();
        if (fadeTimer) stopFade();
        cur().el.pause();
      }
    }
  });

  // Recharge la dernière piste (sans lecture automatique)
  const t = currentTrack(st);
  if (t) {
    const start = restorePosition ?? resumePoint(t);
    restorePosition = undefined;
    void load(t, false, start);
  }

  window.addEventListener('pagehide', () => {
    const c = currentTrack(usePlayer.getState());
    if (c?.kind === 'stories' && position() > 5) persistProgress(c, position());
  });

  setInterval(() => {
    const d = usePlayer.getState().duration;
    if (!('mediaSession' in navigator) || !d) return;
    try {
      navigator.mediaSession.setPositionState({ duration: d, position: Math.min(position(), d), playbackRate: cur().el.playbackRate });
    } catch {
      /* ignore */
    }
  }, 2000);
}
